/**
 * AssetsCoverage - Écran de gouvernance "Couverture des Assets"
 * 
 * RÈGLES NON NÉGOCIABLES :
 * 1. Aucune création ici - on ne fait qu'activer des capacités existantes
 * 2. L'IA ne "suppose" rien - données exclusivement de /actionable-assets et /coverage-summary
 * 3. Un clic = une intention claire - "Lier" mène à une liste d'intégrations compatibles
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Package,
  Globe,
  Link2,
  Unlink,
  ChevronRight,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Zap,
  Info,
  Plus,
  Facebook,
  Linkedin,
  Instagram,
  Youtube,
  Twitter,
  Smartphone,
  Code,
  Box,
  Pencil,
  Trash2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Icônes par type d'intégration
const INTEGRATION_ICONS = {
  google_search_console: { icon: "🔍", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  google_analytics: { icon: "📊", color: "text-orange-400", bg: "bg-orange-500/10" },
  google_sheets: { icon: "📄", color: "text-green-400", bg: "bg-green-500/10" },
  zoho_workdrive: { icon: "☁️", color: "text-blue-400", bg: "bg-blue-500/10" },
  twilio_voice: { icon: "📞", color: "text-purple-400", bg: "bg-purple-500/10" },
  twilio_sms: { icon: "💬", color: "text-red-400", bg: "bg-red-500/10" },
  wordpress: { icon: "🌐", color: "text-blue-500", bg: "bg-blue-500/10" },
  imap_email: { icon: "📧", color: "text-amber-400", bg: "bg-amber-500/10" },
};

// Icônes et config par type d'asset
const ASSET_TYPE_CONFIG = {
  site_web: { icon: Globe, name: "Site Web", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  page_facebook: { icon: Facebook, name: "Page Facebook", color: "text-blue-400", bg: "bg-blue-500/10" },
  page_linkedin: { icon: Linkedin, name: "Page LinkedIn", color: "text-sky-400", bg: "bg-sky-500/10" },
  compte_instagram: { icon: Instagram, name: "Compte Instagram", color: "text-pink-400", bg: "bg-pink-500/10" },
  compte_twitter: { icon: Twitter, name: "Compte Twitter/X", color: "text-zinc-400", bg: "bg-zinc-500/10" },
  chaine_youtube: { icon: Youtube, name: "Chaîne YouTube", color: "text-red-400", bg: "bg-red-500/10" },
  application_mobile: { icon: Smartphone, name: "Application Mobile", color: "text-purple-400", bg: "bg-purple-500/10" },
  api: { icon: Code, name: "API", color: "text-amber-400", bg: "bg-amber-500/10" },
  autre: { icon: Box, name: "Autre", color: "text-zinc-400", bg: "bg-zinc-500/10" },
};

// Mapping des statuts de santé vers couleurs
const HEALTH_STATUS = {
  ok: { 
    color: "bg-emerald-400", 
    textColor: "text-emerald-400",
    borderColor: "border-emerald-500/20 hover:border-emerald-500/40"
  },
  degraded: { 
    color: "bg-amber-400", 
    textColor: "text-amber-400",
    borderColor: "border-amber-500/20 hover:border-amber-500/40"
  },
  error: { 
    color: "bg-red-400", 
    textColor: "text-red-400",
    borderColor: "border-red-500/20 hover:border-red-500/40"
  }
};

// Composant pastille de santé avec tooltip
const HealthIndicator = ({ health, showLabel = false }) => {
  if (!health) return null;
  
  const config = HEALTH_STATUS[health.status] || HEALTH_STATUS.ok;
  
  return (
    <div className="relative group">
      <div className="flex items-center gap-1.5">
        {/* Pastille */}
        <div 
          className={`w-2.5 h-2.5 rounded-full ${config.color}`}
          title={health.tooltip}
        />
        {showLabel && (
          <span className={`text-xs ${config.textColor}`}>{health.label}</span>
        )}
      </div>
      
      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-zinc-800 border border-white/10 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 whitespace-nowrap">
        <p className="text-xs text-white">{health.tooltip}</p>
        {/* Flèche */}
        <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-zinc-800" />
      </div>
    </div>
  );
};

// Composant carte d'asset couvert
const CoveredAssetCard = ({ asset, onUnlink, onEdit, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Déterminer la couleur de bordure selon la santé globale de l'asset
  const assetHealth = asset.health || { status: "ok" };
  const healthConfig = HEALTH_STATUS[assetHealth.status] || HEALTH_STATUS.ok;
  
  // Config du type d'asset
  const typeConfig = ASSET_TYPE_CONFIG[asset.type] || ASSET_TYPE_CONFIG.site_web;
  const IconComponent = typeConfig.icon;
  
  return (
    <div className={`p-4 bg-zinc-900/50 rounded-lg border transition-all ${healthConfig.borderColor}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${typeConfig.bg} flex items-center justify-center flex-shrink-0 relative`}>
            <IconComponent className={`w-5 h-5 ${typeConfig.color}`} />
            {/* Pastille de santé globale sur l'icône */}
            <div className="absolute -top-1 -right-1">
              <HealthIndicator health={assetHealth} />
            </div>
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-white truncate">{asset.nom}</h3>
            <p className="text-xs text-zinc-500 truncate">{asset.url}</p>
            {asset.activite_code && (
              <Badge variant="outline" className="mt-1 text-xs">
                {asset.activite_code}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge className="bg-emerald-500/20 text-emerald-400">
            {asset.integration_count} intégration{asset.integration_count > 1 ? "s" : ""}
          </Badge>
          {/* Boutons édition/suppression */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit({ id: asset.asset_id, ...asset })}
            className="text-zinc-400 hover:text-white h-8 w-8 p-0"
            title="Modifier"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete({ id: asset.asset_id, ...asset })}
            className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => setExpanded(!expanded)}
            className="text-zinc-400 hover:text-white"
          >
            <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
          </Button>
        </div>
      </div>
      
      {/* Intégrations liées (détail) */}
      {expanded && (
        <div className="mt-4 pt-3 border-t border-white/5 space-y-2">
          <p className="text-xs text-zinc-500 uppercase tracking-wide mb-2">Intégrations actives</p>
          {asset.integrations.map((int, idx) => {
            const iconConfig = INTEGRATION_ICONS[int.integration_type] || { icon: "🔗", color: "text-zinc-400", bg: "bg-zinc-500/10" };
            const intHealth = int.health || { status: "ok", tooltip: "Statut inconnu" };
            
            return (
              <div 
                key={idx}
                className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span className={`w-7 h-7 rounded ${iconConfig.bg} flex items-center justify-center text-sm`}>
                    {iconConfig.icon}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white">{int.connection_name}</p>
                      {/* Pastille de santé par intégration */}
                      <HealthIndicator health={intHealth} />
                    </div>
                    <p className="text-xs text-zinc-500">{int.integration_type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {/* Lien vers CTO si dégradé/erreur */}
                  {intHealth.status !== "ok" && (
                    <a 
                      href="/hub" 
                      onClick={(e) => {
                        e.stopPropagation();
                        // Navigate to CTO dashboard in the future
                      }}
                      className="text-xs text-zinc-500 hover:text-purple-400 mr-2"
                      title="Voir les détails techniques"
                    >
                      Voir détails
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onUnlink(int.connection_id, asset.asset_id)}
                    className="text-zinc-400 hover:text-red-400"
                    title="Délier cet asset"
                  >
                    <Unlink className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            );
          })}
          
          {/* Bouton pour lier à d'autres intégrations */}
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full mt-2 text-xs"
            onClick={() => onUnlink(null, asset.asset_id, true)}
          >
            <Link2 className="w-3 h-3 mr-1" />
            Lier à une autre intégration
          </Button>
        </div>
      )}
    </div>
  );
};

// Composant carte d'asset non couvert
const UncoveredAssetCard = ({ asset, onLink, onEdit, onDelete }) => {
  const typeConfig = ASSET_TYPE_CONFIG[asset.type] || ASSET_TYPE_CONFIG.autre;
  const IconComponent = typeConfig.icon;
  
  return (
    <div className="p-4 bg-zinc-900/50 rounded-lg border border-amber-500/20 hover:border-amber-500/40 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-lg ${typeConfig.bg} flex items-center justify-center flex-shrink-0`}>
            <IconComponent className={`w-5 h-5 ${typeConfig.color}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-white truncate">{asset.nom}</h3>
            <p className="text-xs text-zinc-500 truncate">{asset.url || "Pas d'URL"}</p>
            <Badge variant="outline" className="mt-1 text-xs">
              {typeConfig.name}
            </Badge>
          </div>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(asset)}
            className="text-zinc-400 hover:text-white h-8 w-8 p-0"
            title="Modifier"
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(asset)}
            className="text-zinc-400 hover:text-red-400 h-8 w-8 p-0"
            title="Supprimer"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-white/5">
        <p className="text-xs text-amber-400/80 mb-2">
          L'IA ne peut pas agir sur cet asset
        </p>
        <Button 
          size="sm" 
          onClick={() => onLink(asset.id)}
          className="w-full bg-amber-500/20 hover:bg-amber-500/30 text-amber-300"
        >
          <Link2 className="w-4 h-4 mr-2" />
          Lier à une intégration
        </Button>
      </div>
    </div>
  );
};

// Dialog pour lier un asset à une intégration
const LinkAssetDialog = ({ open, onClose, assetId, assetName, connections, onLink }) => {
  const [selectedConnection, setSelectedConnection] = useState("");
  const [linking, setLinking] = useState(false);
  
  // Filtrer les connexions actives
  const activeConnections = connections.filter(c => c.status === "active");
  
  const handleLink = async () => {
    if (!selectedConnection) return;
    
    setLinking(true);
    try {
      await onLink(selectedConnection, assetId);
      onClose();
    } catch (err) {
      console.error("Erreur liaison:", err);
    } finally {
      setLinking(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Link2 className="w-5 h-5 text-purple-400" />
            Lier à une intégration
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Choisissez une intégration active pour couvrir cet asset
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Asset sélectionné */}
          <div className="p-3 bg-zinc-800/50 rounded-lg">
            <p className="text-xs text-zinc-500">Asset sélectionné</p>
            <p className="text-white font-medium">{assetName}</p>
          </div>
          
          {/* Sélection de l'intégration */}
          {activeConnections.length > 0 ? (
            <div className="space-y-2">
              <p className="text-sm text-zinc-400">Intégration disponible</p>
              <Select value={selectedConnection} onValueChange={setSelectedConnection}>
                <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                  <SelectValue placeholder="Choisir une intégration..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-white/10">
                  {activeConnections.map((conn) => {
                    const iconConfig = INTEGRATION_ICONS[conn.integration_type] || { icon: "🔗" };
                    return (
                      <SelectItem 
                        key={conn.id} 
                        value={conn.id}
                        className="text-white hover:bg-zinc-700"
                      >
                        <span className="flex items-center gap-2">
                          <span>{iconConfig.icon}</span>
                          <span>{conn.name}</span>
                          <span className="text-xs text-zinc-500">({conn.integration_type})</span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <p className="text-sm text-amber-400 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Aucune intégration active disponible
              </p>
              <p className="text-xs text-zinc-400 mt-2">
                Configurez d'abord une intégration dans{" "}
                <a href="/management/integrations" className="text-purple-400 hover:underline">
                  Management &gt; Intégrations
                </a>
              </p>
            </div>
          )}
          
          {/* Info */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-xs text-blue-400 flex items-start gap-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Une fois lié, l'IA pourra utiliser cette intégration pour agir sur cet asset 
                (ex: récupérer des données SEO, publier du contenu, etc.)
              </span>
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleLink}
            disabled={!selectedConnection || linking}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {linking ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Link2 className="w-4 h-4 mr-2" />
            )}
            Lier
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Dialog de création/édition d'asset
const AssetFormDialog = ({ open, onClose, asset, onSave }) => {
  const [formData, setFormData] = useState({
    type: "site_web",
    nom: "",
    url: "",
    description: ""
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  
  const isEdit = !!asset?.id;
  
  // Réinitialiser le formulaire quand le dialog s'ouvre
  useEffect(() => {
    if (open) {
      if (asset) {
        setFormData({
          type: asset.type || "site_web",
          nom: asset.nom || "",
          url: asset.url || "",
          description: asset.description || ""
        });
      } else {
        setFormData({ type: "site_web", nom: "", url: "", description: "" });
      }
      setError("");
    }
  }, [open, asset]);
  
  const handleSave = async () => {
    if (!formData.nom.trim()) {
      setError("Le nom est requis");
      return;
    }
    
    setSaving(true);
    setError("");
    
    try {
      await onSave(formData, asset?.id);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };
  
  const assetTypes = Object.entries(ASSET_TYPE_CONFIG).map(([type, config]) => ({
    type,
    ...config
  }));
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            {isEdit ? <Pencil className="w-5 h-5 text-purple-400" /> : <Plus className="w-5 h-5 text-emerald-400" />}
            {isEdit ? "Modifier l'asset" : "Créer un asset"}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {isEdit ? "Modifiez les informations de l'asset" : "Ajoutez un nouvel asset à votre entreprise"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          {/* Type d'asset */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Type d'asset</Label>
            <Select 
              value={formData.type} 
              onValueChange={(v) => setFormData({...formData, type: v})}
            >
              <SelectTrigger className="bg-zinc-800 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-white/10">
                {assetTypes.map((t) => {
                  const IconComponent = t.icon;
                  return (
                    <SelectItem 
                      key={t.type} 
                      value={t.type}
                      className="text-white hover:bg-zinc-700"
                    >
                      <span className="flex items-center gap-2">
                        <IconComponent className={`w-4 h-4 ${t.color}`} />
                        <span>{t.name}</span>
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          {/* Nom */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Nom *</Label>
            <Input
              value={formData.nom}
              onChange={(e) => setFormData({...formData, nom: e.target.value})}
              placeholder="Ex: Site vitrine, Page Facebook..."
              className="bg-zinc-800 border-white/10 text-white"
            />
          </div>
          
          {/* URL */}
          <div className="space-y-2">
            <Label className="text-zinc-400">URL</Label>
            <Input
              value={formData.url}
              onChange={(e) => setFormData({...formData, url: e.target.value})}
              placeholder="https://..."
              className="bg-zinc-800 border-white/10 text-white"
            />
          </div>
          
          {/* Description */}
          <div className="space-y-2">
            <Label className="text-zinc-400">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Description optionnelle..."
              className="bg-zinc-800 border-white/10 text-white resize-none"
              rows={2}
            />
          </div>
          
          {/* Erreur */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : isEdit ? (
              <Pencil className="w-4 h-4 mr-2" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            {isEdit ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Composant principal
export default function AssetsCoverage({ currentEntreprise }) {
  // État - données brutes des APIs
  const [coverageSummary, setCoverageSummary] = useState(null);
  const [actionableAssets, setActionableAssets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // État - dialogs
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [selectedAssetForLink, setSelectedAssetForLink] = useState(null);
  const [assetFormOpen, setAssetFormOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  
  // Headers avec tenant
  const getHeaders = useCallback(() => {
    const token = localStorage.getItem("session_token");
    const headers = { Authorization: `Bearer ${token}` };
    // Utiliser l'ID du tenant pour filtrage
    if (currentEntreprise?.id) {
      Object.assign(headers, enteteTenant(currentEntreprise.id));
    }
    return headers;
  }, [currentEntreprise]);
  
  // Chargement des données - EXCLUSIVEMENT depuis les APIs
  const loadData = useCallback(async () => {
    try {
      const headers = getHeaders();
      const [summaryRes, actionableRes, connectionsRes] = await Promise.all([
        axios.get(`${API}/api/connections/coverage-summary`, { headers }),
        axios.get(`${API}/api/connections/actionable-assets`, { headers }),
        axios.get(`${API}/api/connections`, { headers })
      ]);
      
      setCoverageSummary(summaryRes.data);
      setActionableAssets(actionableRes.data.actionable_assets || []);
      setConnections(connectionsRes.data || []);
    } catch (err) {
      console.error("Erreur chargement données:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getHeaders]);
  
  useEffect(() => {
    loadData();
  }, [loadData]);
  
  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };
  
  // Actions
  const handleLinkAsset = async (connectionId, assetId) => {
    try {
      await axios.post(
        `${API}/api/connections/${connectionId}/link-asset/${assetId}`,
        {},
        { headers: getHeaders() }
      );
      await loadData();
    } catch (err) {
      console.error("Erreur liaison:", err);
      throw err;
    }
  };
  
  const handleUnlinkAsset = async (connectionId, assetId, openLinkDialog = false) => {
    if (openLinkDialog) {
      // Ouvrir le dialog pour lier à une autre intégration
      const asset = actionableAssets.find(a => a.asset_id === assetId);
      setSelectedAssetForLink({ id: assetId, name: asset?.nom || "Asset" });
      setLinkDialogOpen(true);
      return;
    }
    
    try {
      await axios.delete(
        `${API}/api/connections/${connectionId}/unlink-asset/${assetId}`,
        { headers: getHeaders() }
      );
      await loadData();
    } catch (err) {
      console.error("Erreur déliaison:", err);
    }
  };
  
  const openLinkDialog = (assetId) => {
    const asset = coverageSummary?.uncovered?.find(a => a.id === assetId);
    setSelectedAssetForLink({ id: assetId, name: asset?.nom || "Asset" });
    setLinkDialogOpen(true);
  };
  
  // Gestion des assets (CRUD)
  const handleSaveAsset = async (formData, assetId) => {
    if (assetId) {
      // Update
      await axios.put(
        `${API}/api/connections/assets/${assetId}`,
        formData,
        { headers: getHeaders() }
      );
    } else {
      // Create
      await axios.post(
        `${API}/api/connections/assets/create`,
        formData,
        { headers: getHeaders() }
      );
    }
    await loadData();
  };
  
  const handleEditAsset = (asset) => {
    setEditingAsset(asset);
    setAssetFormOpen(true);
  };
  
  const handleDeleteAsset = (asset) => {
    setAssetToDelete(asset);
    setDeleteDialogOpen(true);
  };
  
  const confirmDeleteAsset = async () => {
    if (!assetToDelete) return;
    
    try {
      await axios.delete(
        `${API}/api/connections/assets/${assetToDelete.id}?force=true`,
        { headers: getHeaders() }
      );
      await loadData();
    } catch (err) {
      console.error("Erreur suppression:", err);
    } finally {
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
    }
  };
  
  const openCreateDialog = () => {
    setEditingAsset(null);
    setAssetFormOpen(true);
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }
  
  const summary = coverageSummary?.summary || {};
  const coveredAssets = actionableAssets || [];
  const uncoveredAssets = coverageSummary?.uncovered || [];
  
  return (
    <div className="space-y-6" data-testid="assets-coverage-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Couverture des Assets</h2>
            <p className="text-sm text-zinc-400">Sur quoi l'IA peut-elle agir ?</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            size="sm"
            onClick={openCreateDialog}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="create-asset-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Créer un asset
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-coverage-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>
      
      {/* Résumé de couverture */}
      <Card className="bg-zinc-900/50 border-white/5">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Jauge de couverture */}
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    className="text-zinc-800"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="none"
                    strokeDasharray={`${(summary.coverage_rate || 0) * 2.51} 251`}
                    className={summary.coverage_rate >= 100 ? "text-emerald-400" : summary.coverage_rate >= 50 ? "text-amber-400" : "text-red-400"}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-2xl font-bold text-white">{summary.coverage_rate || 0}%</span>
                </div>
              </div>
              
              {/* Stats */}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-zinc-400">Assets couverts :</span>
                  <span className="text-white font-medium">{summary.covered_assets || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-zinc-400">Assets non couverts :</span>
                  <span className="text-white font-medium">{summary.uncovered_assets || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-zinc-400" />
                  <span className="text-zinc-400">Total :</span>
                  <span className="text-white font-medium">{summary.total_assets || 0}</span>
                </div>
              </div>
            </div>
            
            {/* Message de recommandation */}
            {coverageSummary?.recommendation && (
              <div className="max-w-sm p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-xs text-blue-400 flex items-start gap-2">
                  <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{coverageSummary.recommendation}</span>
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Section Assets Actionnables (couverts) */}
      {coveredAssets.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-emerald-400" />
            <h3 className="text-lg font-medium text-white">
              Assets Actionnables
              <span className="ml-2 text-sm text-zinc-500">({coveredAssets.length})</span>
            </h3>
          </div>
          
          <div className="grid gap-3 md:grid-cols-2">
            {coveredAssets.map((asset) => (
              <CoveredAssetCard
                key={asset.asset_id}
                asset={asset}
                onUnlink={handleUnlinkAsset}
                onEdit={handleEditAsset}
                onDelete={handleDeleteAsset}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* Section Assets Non Couverts */}
      {uncoveredAssets.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            <h3 className="text-lg font-medium text-white">
              Assets Non Couverts
              <span className="ml-2 text-sm text-zinc-500">({uncoveredAssets.length})</span>
            </h3>
          </div>
          
          <div className="grid gap-3 md:grid-cols-2">
            {uncoveredAssets.map((asset) => (
              <UncoveredAssetCard
                key={asset.id}
                asset={asset}
                onLink={openLinkDialog}
                onEdit={handleEditAsset}
                onDelete={handleDeleteAsset}
              />
            ))}
          </div>
        </div>
      )}
      
      {/* État vide */}
      {coveredAssets.length === 0 && uncoveredAssets.length === 0 && (
        <Card className="bg-zinc-900/50 border-white/5">
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Aucun asset configuré</h3>
            <p className="text-sm text-zinc-400 mb-4">
              Les assets sont des entités métier sur lesquelles l'IA peut agir (sites web, applications, etc.)
            </p>
            <Button 
              size="sm"
              onClick={openCreateDialog}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un asset
            </Button>
          </CardContent>
        </Card>
      )}
      
      {/* Dialog de liaison */}
      <LinkAssetDialog
        open={linkDialogOpen}
        onClose={() => {
          setLinkDialogOpen(false);
          setSelectedAssetForLink(null);
        }}
        assetId={selectedAssetForLink?.id}
        assetName={selectedAssetForLink?.name}
        connections={connections}
        onLink={handleLinkAsset}
      />
      
      {/* Dialog de création/édition d'asset */}
      <AssetFormDialog
        open={assetFormOpen}
        onClose={() => {
          setAssetFormOpen(false);
          setEditingAsset(null);
        }}
        asset={editingAsset}
        onSave={handleSaveAsset}
      />
      
      {/* Dialog de confirmation de suppression */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer l'asset ?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Êtes-vous sûr de vouloir supprimer "{assetToDelete?.nom}" ? 
              Cette action est irréversible et retirera tous les liens avec les intégrations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteAsset}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
